// components/domain/AddSubdomainForm.tsx
import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { FiCheck, FiX, FiLoader } from 'react-icons/fi';
import { getDnsRecords } from '../../api/domainApi';

// Validation schema for adding a subdomain
const SubdomainSchema = Yup.object().shape({
    subdomain: Yup.string()
        .matches(
            /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]$/,
            'Enter a valid subdomain name (letters, numbers, and hyphens only)'
        )
        .required('Subdomain name is required'),
    mainCnameDest: Yup.string()
        .required('Main CNAME destination is required'),
    hubCnameDest: Yup.string()
        .required('Hub CNAME destination is required')
});

// Debounce function to avoid excessive API calls
// Debounce function to avoid excessive API calls
const useDebounce = <T,>(value: T, delay: number): T => {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

interface SubdomainFormValues {
    subdomain: string;
    mainCnameDest: string;
    hubCnameDest: string;
}

// Subdomain availability checker component
const SubdomainChecker: React.FC<{ domain: string }> = ({ domain }) => {
    const { values, setFieldError, setFieldTouched } = useFormikContext<SubdomainFormValues>();
    const [checking, setChecking] = useState(false);
    const [exists, setExists] = useState(false);
    const debouncedSubdomain = useDebounce(values.subdomain, 800); // 800ms debounce

    useEffect(() => {
        const checkSubdomainAvailability = async () => {
            if (!debouncedSubdomain || debouncedSubdomain.length < 2) return;

            setChecking(true);
            try {
                // Check if subdomain already exists
                const response = await getDnsRecords(domain, 'CNAME', debouncedSubdomain);
                const exists = response?.data && response.data.length > 0;

                setExists(exists);
                if (exists) {
                    setFieldError('subdomain', `Subdomain ${debouncedSubdomain}.${domain} already exists`);
                    setFieldTouched('subdomain', true, false);
                }
            } catch (error) {
                console.error('Error checking subdomain:', error);
            } finally {
                setChecking(false);
            }
        };

        checkSubdomainAvailability();
    }, [debouncedSubdomain, domain, setFieldError, setFieldTouched]);

    if (!values.subdomain || values.subdomain.length < 2) return null;

    if (checking) {
        return (
            <div className="mt-1 text-sm text-blue-600 dark:text-blue-400 flex items-center">
                <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Checking availability...
            </div>
        );
    }

    if (exists) {
        return (
            <div className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center">
                <FiX className="mr-1" />
                This subdomain is already taken
            </div>
        );
    }

    return (
        <div className="mt-1 text-sm text-green-600 dark:text-green-400 flex items-center">
            <FiCheck className="mr-1" />
            Subdomain available
        </div>
    );
};

interface AddSubdomainFormProps {
    domain: string; // Parent domain (ontoris.com)
    onSubmit: (values: any) => Promise<void>;
    onCancel: () => void;
    loading: boolean;
}

const AddSubdomainForm: React.FC<AddSubdomainFormProps> = ({
    domain,
    onSubmit,
    onCancel,
    loading
}) => {
    // Default CNAME destinations
    const DEFAULT_MAIN_CNAME_DEST = 'webbffui.grayglacier-35f15573.switzerlandnorth.azurecontainerapps.io.';
    const DEFAULT_HUB_CNAME_DEST = 'notification.grayglacier-35f15573.switzerlandnorth.azurecontainerapps.io.';

    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
                Add Subdomain for {domain}
            </h2>

            <Formik
                initialValues={{
                    subdomain: '',
                    mainCnameDest: DEFAULT_MAIN_CNAME_DEST,
                    hubCnameDest: DEFAULT_HUB_CNAME_DEST
                }}
                validationSchema={SubdomainSchema}
                onSubmit={async (values) => {
                    // Prepare the DNS records data
                    const dnsData = {
                        // The main subdomain CNAME record
                        cnameRecords: [
                            {
                                name: values.subdomain,
                                value: values.mainCnameDest
                            },
                            // The hub.subdomain CNAME record
                            {
                                name: `hub.${values.subdomain}`,
                                value: values.hubCnameDest
                            }
                        ]
                    };

                    await onSubmit({
                        subdomain: values.subdomain,
                        dnsData: dnsData
                    });
                }}
            >
                {({ isValid, dirty, values }) => (
                    <Form className="space-y-4">
                        <div>
                            <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Subdomain Name
                            </label>
                            <div className="mt-1 flex rounded-md shadow-sm">
                                <Field
                                    type="text"
                                    name="subdomain"
                                    id="subdomain"
                                    placeholder="mysubdomain"
                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-l-md"
                                />
                                <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-300 sm:text-sm">
                                    .{domain}
                                </span>
                            </div>
                            <ErrorMessage name="subdomain" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                            <SubdomainChecker domain={domain} />
                        </div>

                        <div>
                            <label htmlFor="mainCnameDest" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Main CNAME Destination
                            </label>
                            <div className="mt-1">
                                <Field
                                    type="text"
                                    name="mainCnameDest"
                                    id="mainCnameDest"
                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                />
                            </div>
                            <ErrorMessage name="mainCnameDest" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                        </div>

                        <div>
                            <label htmlFor="hubCnameDest" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Hub CNAME Destination
                            </label>
                            <div className="mt-1">
                                <Field
                                    type="text"
                                    name="hubCnameDest"
                                    id="hubCnameDest"
                                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                />
                            </div>
                            <ErrorMessage name="hubCnameDest" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                        </div>

                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            This will create two CNAME records:
                            <ul className="list-disc list-inside mt-1 ml-2">
                                <li><span className="font-mono">{values.subdomain || 'subdomain'}.{domain}</span> ? points to the main CNAME destination</li>
                                <li><span className="font-mono">hub.{values.subdomain || 'subdomain'}.{domain}</span> ? points to the hub CNAME destination</li>
                            </ul>
                        </div>

                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                type="button"
                                onClick={onCancel}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                <FiX className="mr-2" />
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={!(isValid && dirty) || loading}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-400 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <svg
                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Adding...
                                    </>
                                ) : (
                                    <>
                                        <FiCheck className="mr-2" />
                                        Add Subdomain
                                    </>
                                )}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default AddSubdomainForm;